import React, { useEffect, useState } from "react";
import clsx from "clsx";
import { ClientValue, ExternalMatterData, Matter } from "../../data-types";
import { Modal, Switch, Tooltip } from "antd";
import { Spinner } from "../spinner";
import { ExternalMatterSelector } from "../matter-external-or-custom-editor";

import styles from "./matter-external-matter-link.css";

const {
  ["wrapper"]: wrapper,
  ["wrapper-use-external-matter"]: wrapperUseExternalMatter,
  ["custom-matter-items-holder"]: customMatterItemsHolder,
  ["active"]: customMatterItemsHolderActive,
  ["switch"]: switchEl,
  ["external-matter-select-holder"]: externalMatterSelectHolder,
} = styles as any;

type ExternalMatterId = number | null;

declare type MatterExternalMatterLinkProps = {
  className?: string;
  client: ClientValue | null;
  disabled?: boolean;
  externalMatterData: ExternalMatterData;
  externalMatterId?: ExternalMatterId;
  matter?: Matter;
  onChange?: (externalMatterId: ExternalMatterId) => void | Promise<void>;
  pmsName: string;
  isEditMode?: boolean;
  selectedCurrencyCode?: string;
  showSwitch?: boolean;
  allowEdit?: boolean;
};

export const MatterExternalMatterLink: React.FC<MatterExternalMatterLinkProps> = ({
  className,
  client,
  disabled = false,
  externalMatterData,
  externalMatterId,
  matter,
  onChange,
  pmsName,
  selectedCurrencyCode,
  isEditMode,
  showSwitch = true,
}) => {
  const [showUnlinkConfirmation, setShowUnlinkConfirmation] = useState<boolean>(false);

  const [useExternalMatter, setUseExternalMatter] = useState<boolean>(
    !!externalMatterId || !showSwitch
  );

  useEffect(() => {
    setUseExternalMatter(!!externalMatterId || !showSwitch);
  }, [externalMatterId]);

  const doOnMatterExternalMatterUpdate = ({
    externalMatterId = null,
  }: {
    externalMatterId: ExternalMatterId;
  }) => {
    onChange && onChange(externalMatterId);
  };

  const [loading, setLoading] = useState<boolean>(false);

  const externalClientExists = !!client?.externalId || !!client?.externalReference;

  return (
    <div
      className={clsx(
        `${className} ${wrapper}`,
        useExternalMatter && externalMatterId && wrapperUseExternalMatter
      )}
    >
      <Spinner show={loading === true} message={"Updating Matter..."}>
        <div
          className={clsx(
            customMatterItemsHolder,
            !useExternalMatter && !externalMatterId && customMatterItemsHolderActive
          )}
        >
          {
            <>
              {showSwitch && (
                <Tooltip
                  title={
                    !externalClientExists
                      ? `The selected client must exist in ${pmsName} before the matter can be linked.`
                      : ""
                  }
                >
                  <div>
                    <Switch
                      className={switchEl}
                      checked={useExternalMatter || !!externalMatterId}
                      disabled={disabled || !externalClientExists}
                      onChange={async (useExternalMatterId: boolean) => {
                        if (!useExternalMatterId) {
                          if (externalMatterId) {
                            setShowUnlinkConfirmation(true);
                          } else {
                            doOnMatterExternalMatterUpdate({
                              externalMatterId: null,
                            });
                            setUseExternalMatter(false);
                          }
                        } else {
                          setLoading(true);
                          try {
                            setUseExternalMatter(true);
                          } finally {
                            setLoading(false);
                          }
                        }
                      }}
                    />
                  </div>
                </Tooltip>
              )}

              <Modal
                title={`Unlink ${pmsName} Matter`}
                centered
                open={showUnlinkConfirmation}
                onOk={async () => {
                  setLoading(true);
                  try {
                    doOnMatterExternalMatterUpdate({
                      externalMatterId: null,
                    });
                    setUseExternalMatter(false);
                  } finally {
                    setShowUnlinkConfirmation(false);
                    setLoading(false);
                  }
                }}
                onCancel={() => {
                  setShowUnlinkConfirmation(false);
                }}
              >
                <p>
                  Are you sure you want to unlink this Matter from the selected {`${pmsName} `}
                  Matter?
                </p>
              </Modal>
            </>
          }
          <div className={externalMatterSelectHolder}>
            <ExternalMatterSelector
              isEditMode={isEditMode}
              selectedCurrencyCode={selectedCurrencyCode}
              useExternalMatter={useExternalMatter}
              clientId={client?.id || null}
              defaultValue={externalMatterId || undefined}
              disabled={disabled || !useExternalMatter || !externalClientExists}
              externalMatterData={externalMatterData}
              matter={matter}
              onMatterExternalMatterUpdate={doOnMatterExternalMatterUpdate}
              pmsName={pmsName}
            />
          </div>
        </div>
      </Spinner>
    </div>
  );
};
